import { Service, signal, inject } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  AuthErrorCodes,
} from '@angular/fire/auth';

export interface AuthState {
  loading: boolean;
  error: string | null;
}

@Service()
export class AdminAuthService {
  private readonly auth = inject(Auth);

  readonly state = signal<AuthState>({ loading: false, error: null });

  async login(email: string, password: string): Promise<boolean> {
    this.state.set({ loading: true, error: null });
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
      this.state.set({ loading: false, error: null });
      return true;
    } catch (err: any) {
      this.state.set({ loading: false, error: this.mapError(err?.code) });
      return false;
    }
  }

  async sendPasswordReset(email: string): Promise<boolean> {
    this.state.set({ loading: true, error: null });
    try {
      await sendPasswordResetEmail(this.auth, email);
      this.state.set({ loading: false, error: null });
      return true;
    } catch (err: any) {
      this.state.set({ loading: false, error: this.mapError(err?.code) });
      return false;
    }
  }

  async logout() {
    await signOut(this.auth);
  }

  async getIdToken(): Promise<string | null> {
    return this.auth.currentUser?.getIdToken() ?? null;
  }

  get currentUser() {
    return this.auth.currentUser;
  }

  clearError() {
    this.state.set({ ...this.state(), error: null });
  }

  private mapError(code: string | undefined): string {
    switch (code) {
      case AuthErrorCodes.INVALID_EMAIL:
      case AuthErrorCodes.INVALID_PASSWORD:
      case AuthErrorCodes.USER_DELETED:
        return 'Email or password is incorrect.';
      case AuthErrorCodes.USER_DISABLED:
        return 'This account has been disabled. Contact your administrator.';
      case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
        return 'Too many attempts. Wait a few minutes and try again.';
      case AuthErrorCodes.NETWORK_REQUEST_FAILED:
        return 'Network error. Check your connection and try again.';
      default:
        return 'Something went wrong signing you in. Please try again.';
    }
  }
}
